interface MinimalProcedure {
    OrderProcedureID: number;
}

interface MinimalReport {
    OrderProcedureIds: number[];
    ReportID: number;
}

interface ReportAlignmentOrder<TProcedure extends MinimalProcedure, TReport extends MinimalReport> {
    OrderProceduresInfos: TProcedure[];
    OrderReportsInfos: TReport[];
}

export interface AlignedReportValue<T> {
    key: string;
    value: T;
}

const procedureKey = (prefix: string, procedureId: number): string =>
    `${prefix}-${procedureId}`;

const unmatchedKey = (prefix: string, reportId: number): string =>
    `${prefix}-unmatched-${reportId}`;

export const buildAlignedReportValuesWithKeys = <TProcedure extends MinimalProcedure, TReport extends MinimalReport, T>(
    order: ReportAlignmentOrder<TProcedure, TReport>,
    selector: (report: TReport) => T,
    fallback: T | ((procedure: TProcedure) => T),
    keyPrefix: string,
): readonly AlignedReportValue<T>[] => {
    const procedures = order.OrderProceduresInfos ?? [];
    const reports = order.OrderReportsInfos ?? [];

    const isFallbackFn = (f: T | ((p: TProcedure) => T)): f is (p: TProcedure) => T =>
        typeof f === 'function';

    const resolveFallback = (procedure: TProcedure): T =>
        isFallbackFn(fallback) ? fallback(procedure) : fallback;

    const rowsAlignedToProcedure: AlignedReportValue<T>[] = procedures.map((procedure) => {
        const report = reports.find((r) => r.OrderProcedureIds?.includes(procedure.OrderProcedureID));
        return {
            key: procedureKey(keyPrefix, procedure.OrderProcedureID),
            value: report ? selector(report) : resolveFallback(procedure),
        };
    });

    const unmatchedReports: AlignedReportValue<T>[] = reports
        .filter((r) => !procedures.some((p) => r.OrderProcedureIds?.includes(p.OrderProcedureID)))
        .map((report) => ({
            key: unmatchedKey(keyPrefix, report.ReportID),
            value: selector(report),
        }));

    return [...rowsAlignedToProcedure, ...unmatchedReports];
};

export const buildAlignedReports = <TProcedure extends MinimalProcedure, TReport extends MinimalReport>(
    order: ReportAlignmentOrder<TProcedure, TReport>,
): (TReport | null)[] => {
    return buildAlignedReportValuesWithKeys(order, (r) => r as TReport | null, null, 'aligned-report')
        .map((row) => row.value);
};

export const buildAlignedReportValues = <TProcedure extends MinimalProcedure, TReport extends MinimalReport, T>(
    order: ReportAlignmentOrder<TProcedure, TReport>,
    selector: (report: TReport) => T,
    fallback: T,
): T[] => {
    return buildAlignedReportValuesWithKeys(order, selector, fallback, '').map((row) => row.value);
};
